import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import { buildMonthKeys, monthLabel, monthPhase, currentMonthKey } from '../lib/months';
import { sortReleasesByStart } from '../lib/releases';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { cellOf, type CapMap } from '../lib/capacity';
import { fmtInt } from '../lib/format';
import { useT, format } from '../i18n';

type Props = { open: boolean; onClose: () => void };

// Modal zum Pflegen der Team × Release × Monat × {Stories, Bugs}-Kapazität.
//
// Datenfluss: Beim Öffnen wird ein Snapshot in einen lokalen Draft kopiert.
// Eingaben mutieren nur den Draft. "Speichern" schreibt alles atomic via
// useConfig.setCapacity → POST /api/config.
export function CapacityDialog({ open, onClose }: Props) {
  const capacity   = useConfig(s => s.capacityByTeam);
  const releaseCal = useConfig(s => s.releaseCal);
  const setCapacity = useConfig(s => s.setCapacity);
  const rows = useData(s => s.rows);

  const months = useMemo(() => buildMonthKeys(), []);
  const cur = currentMonthKey();

  // Releases aus dem Kalender. Wenn keiner gepflegt, kann man trotzdem
  // Kapa pflegen — Fallback auf "Allgemein"
  const releases = useMemo(() => {
    const fromCal = sortReleasesByStart(releaseCal);
    return fromCal.length ? fromCal : ['Allgemein'];
  }, [releaseCal]);

  // Teams aus den geladenen Rows, häufigste zuerst. Plus eventuelle Teams,
  // die bereits Kapazität haben, aber keine Issues
  const teams = useMemo(() => {
    const fromRows  = sortedByCount(countBy(rows, 'Team Name')).map(([n]) => n);
    const fromCapa  = Object.keys(capacity);
    const all = new Set([...fromRows, ...fromCapa]);
    return Array.from(all).sort();
  }, [rows, capacity]);

  // Lokale State
  const [team, setTeam]       = useState<string>('');
  const [release, setRelease] = useState<string>(releases[0] ?? '');
  const [draft, setDraft]     = useState<CapMap>({});
  const [fill, setFill]       = useState<string>('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const tt = useT();

  // Beim Öffnen: Snapshot in Draft kopieren, Defaults setzen
  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(capacity)));
      setTeam(t => t || teams[0] || '');
      setRelease(r => r || releases[0] || '');
      setError(null);
    }
  }, [open, capacity, teams, releases]);

  if (!open) return null;

  // Aktuelle Monats-Werte für die gewählte Team/Release-Kombi
  const months_of_release = draft[team]?.[release] ?? {};

  function patchCell(monthKey: string, field: 's' | 'b', value: string) {
    const n = parseFloat(value.replace(',', '.'));
    const v = isFinite(n) && n >= 0 ? Math.round(n) : 0;
    setDraft(d => {
      const next: CapMap = { ...d };
      const t = next[team] ? { ...next[team] } : {};
      const r = t[release] ? { ...t[release] } : {};
      const cell = r[monthKey];
      const cur = (typeof cell === 'object' && cell != null) ? cell : { s: 0, b: 0 };
      const nextCell = { ...cur, [field]: v };
      if (nextCell.s === 0 && nextCell.b === 0) delete r[monthKey];
      else                                       r[monthKey] = nextCell;
      if (Object.keys(r).length === 0) delete t[release];
      else                              t[release] = r;
      if (Object.keys(t).length === 0) delete next[team];
      else                              next[team] = t;
      return next;
    });
  }

  function applyFillToAll(field: 's' | 'b' | 'both') {
    const n = parseFloat(fill.replace(',', '.'));
    if (!isFinite(n) || n < 0) return;
    const v = Math.round(n);
    setDraft(d => {
      const next: CapMap = { ...d };
      const t = next[team] ? { ...next[team] } : {};
      const r = t[release] ? { ...t[release] } : {};
      for (const mk of months) {
        const cell = r[mk];
        const cur = (typeof cell === 'object' && cell != null) ? cell : { s: 0, b: 0 };
        const nextCell = field === 'both'
          ? { s: v, b: v }
          : field === 's'
            ? { s: v, b: cur.b ?? 0 }
            : { s: cur.s ?? 0, b: v };
        if (nextCell.s === 0 && nextCell.b === 0) delete r[mk];
        else                                       r[mk] = nextCell;
      }
      if (Object.keys(r).length === 0) delete t[release];
      else                              t[release] = r;
      if (Object.keys(t).length === 0) delete next[team];
      else                              next[team] = t;
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await setCapacity(draft);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const sumStories = months.reduce((acc, mk) => acc + cellOf(months_of_release[mk], 's'), 0);
  const sumBugs    = months.reduce((acc, mk) => acc + cellOf(months_of_release[mk], 'b'), 0);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <GroupsIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{tt.capacity.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {tt.capacity.subtitle}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{tt.capacity.teamLabel}</InputLabel>
            <Select value={team} label={tt.capacity.teamLabel} onChange={(e) => setTeam(e.target.value)}>
              {teams.length === 0 && <MenuItem value=""><em>{tt.capacity.noTeams}</em></MenuItem>}
              {teams.map(tName => <MenuItem key={tName} value={tName}>{tName}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{tt.capacity.releaseLabel}</InputLabel>
            <Select value={release} label={tt.capacity.releaseLabel} onChange={(e) => setRelease(e.target.value)}>
              {releases.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label={tt.capacity.quickFill}
              type="number"
              value={fill}
              onChange={(e) => setFill(e.target.value)}
              slotProps={{ htmlInput: { min: 0, step: 1, style: { width: 80 } } }}
            />
            <Button size="small" variant="outlined" disabled={!fill || !team} onClick={() => applyFillToAll('s')}>
              {tt.capacity.fillStories}
            </Button>
            <Button size="small" variant="outlined" disabled={!fill || !team} onClick={() => applyFillToAll('b')}>
              {tt.capacity.fillBugs}
            </Button>
            <Button size="small" variant="outlined" disabled={!fill || !team} onClick={() => applyFillToAll('both')}>
              {tt.capacity.fillBoth}
            </Button>
          </Stack>
        </Stack>

        {!team ? (
          <Alert severity="info">{tt.capacity.pickTeamHint}</Alert>
        ) : (
          <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ minWidth: months.length * 90 + 80, display: 'grid', gridTemplateColumns: `120px repeat(${months.length}, minmax(80px, 1fr))` }}>
              <Box sx={{ p: 1, borderRight: 1, borderColor: 'divider', borderBottom: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{tt.capacity.monthHeader}</Typography>
              </Box>
              {months.map(mk => {
                const phase = monthPhase(mk, cur);
                return (
                  <Box key={mk} sx={{
                    p: 1,
                    borderBottom: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    textAlign: 'center',
                    bgcolor: phase === 'now' ? 'primary.main' : 'transparent',
                    color:   phase === 'now' ? 'primary.contrastText' : 'text.primary',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: phase === 'now' ? 700 : 500 }}>
                      {monthLabel(mk)}
                    </Typography>
                  </Box>
                );
              })}

              {/* Stories-Zeile */}
              <Box sx={{ p: 1, borderRight: 1, borderColor: 'divider', borderBottom: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{tt.capacity.storiesLabel}</Typography>
              </Box>
              {months.map(mk => {
                const cell = months_of_release[mk];
                const cur = (typeof cell === 'object' && cell != null) ? cell : null;
                return (
                  <Box key={mk + '-s'} sx={{ borderLeft: 1, borderBottom: 1, borderColor: 'divider', p: 0.5 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={cur?.s ?? ''}
                      onChange={(e) => patchCell(mk, 's', e.target.value)}
                      placeholder="0"
                      slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'right', fontSize: 12, padding: '4px 6px' } } }}
                    />
                  </Box>
                );
              })}

              {/* Bugs-Zeile */}
              <Box sx={{ p: 1, borderRight: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#c62828' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{tt.capacity.bugsLabel}</Typography>
              </Box>
              {months.map(mk => {
                const cell = months_of_release[mk];
                const cur = (typeof cell === 'object' && cell != null) ? cell : null;
                return (
                  <Box key={mk + '-b'} sx={{ borderLeft: 1, borderColor: 'divider', p: 0.5 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={cur?.b ?? ''}
                      onChange={(e) => patchCell(mk, 'b', e.target.value)}
                      placeholder="0"
                      slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'right', fontSize: 12, padding: '4px 6px' } } }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {team && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {format(tt.capacity.sumFor, { team, release })}
            </Typography>
            <Chip size="small" label={format(tt.capacity.storiesChip, { value: fmtInt(sumStories) })} sx={{ bgcolor: '#2e7d3214', color: '#2e7d32', fontWeight: 700 }} />
            <Chip size="small" label={format(tt.capacity.bugsChip, { value: fmtInt(sumBugs) })}      sx={{ bgcolor: '#c6282814', color: '#c62828', fontWeight: 700 }} />
            <Chip size="small" label={format(tt.capacity.totalChip, { value: fmtInt(sumStories + sumBugs) })} variant="outlined" />
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={busy}>{tt.common.close}</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? tt.common.saving : tt.common.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
